import { Injectable } from '@angular/core';

/**
 * Secure File Validation Service
 * 
 * Provides comprehensive file validation including magic number verification,
 * content analysis, and security checks to prevent malicious file uploads.
 */
@Injectable({
  providedIn: 'root'
})
export class FileValidationService {

  // Magic number signatures for allowed file types
  private readonly MAGIC_NUMBERS = {
    // Image formats
    'image/jpeg': [
      [0xFF, 0xD8, 0xFF], // JPEG
    ],
    'image/png': [
      [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], // PNG
    ],
    'image/gif': [
      [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], // GIF87a
      [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], // GIF89a
    ],
    'image/webp': [
      [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x57, 0x45, 0x42, 0x50], // RIFF????WEBP
    ],
    'image/bmp': [
      [0x42, 0x4D], // BM
    ],
    'image/svg+xml': [
      [0x3C, 0x3F, 0x78, 0x6D, 0x6C], // <?xml
      [0x3C, 0x73, 0x76, 0x67], // <svg
    ],
    // Video formats
    'video/mp4': [
      [null, null, null, null, 0x66, 0x74, 0x79, 0x70], // ????ftyp
    ],
    'video/avi': [
      [0x52, 0x49, 0x46, 0x46, null, null, null, null, 0x41, 0x56, 0x49, 0x20], // RIFF????AVI 
    ],
    'video/mov': [
      [null, null, null, null, 0x6D, 0x6F, 0x6F, 0x76], // ????moov
      [null, null, null, null, 0x66, 0x72, 0x65, 0x65], // ????free
    ],
    'video/webm': [
      [0x1A, 0x45, 0xDF, 0xA3], // WebM
    ],
    // PDF format
    'application/pdf': [
      [0x25, 0x50, 0x44, 0x46], // %PDF
    ]
  };

  // Maximum file sizes (in bytes)
  private readonly MAX_FILE_SIZES: Record<string, number> = {
    'image': 10 * 1024 * 1024, // 10MB for images
    'video': 100 * 1024 * 1024, // 100MB for videos
    'pdf': 20 * 1024 * 1024, // 20MB for PDFs
  };

  // Dangerous file extensions and content patterns
  private readonly DANGEROUS_PATTERNS = [
    /<script[\s\S]*?>[\s\S]*?<\/script>/gi, // Script tags
    /javascript:/gi, // JavaScript protocol
    /vbscript:/gi, // VBScript protocol
    /on\w+\s*=/gi, // Event handlers (onclick, onload, etc.)
    /<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, // Iframe tags
    /<object[\s\S]*?>[\s\S]*?<\/object>/gi, // Object tags
    /<embed[\s\S]*?>[\s\S]*?<\/embed>/gi, // Embed tags
  ];

  private readonly DANGEROUS_EXTENSIONS = [
    'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar', 'php', 'asp', 'aspx', 'jsp'
  ];

  constructor() {}

  /**
   * Comprehensive file validation
   */
  async validateFile(file: File): Promise<FileValidationResult> {
    const result: FileValidationResult = {
      isValid: false,
      errors: [],
      warnings: [],
      detectedType: null,
      securityRisk: 'LOW'
    };

    try {
      // 1. Basic file properties validation
      this.validateBasicProperties(file, result);

      // 2. File extension validation
      this.validateFileExtension(file, result);

      // 3. MIME type validation
      this.validateMimeType(file, result);

      // 4. Magic number validation (most important)
      await this.validateMagicNumbers(file, result);

      // 5. Content security scan
      await this.scanFileContent(file, result);

      // 6. File size validation
      this.validateFileSize(file, result);

      // 7. Overall security assessment
      this.assessSecurityRisk(result);

      // Determine if file is valid
      result.isValid = result.errors.length === 0 && 
                      result.securityRisk !== 'CRITICAL' && 
                      result.securityRisk !== 'HIGH';

    } catch (error) {
      result.errors.push(`Validation error: ${error}`);
      result.securityRisk = 'CRITICAL';
    }

    return result;
  }

  /**
   * Validate basic file properties
   */
  private validateBasicProperties(file: File, result: FileValidationResult): void {
    if (!file) {
      result.errors.push('No file provided');
      return;
    }

    if (!file.name || file.name.trim() === '') {
      result.errors.push('File name is empty');
    }

    if (file.size === 0) {
      result.errors.push('File is empty');
    }

    if (file.size > 200 * 1024 * 1024) { // 200MB absolute limit
      result.errors.push('File is too large (maximum 200MB)');
    }
  }

  /**
   * Validate file extension
   */
  private validateFileExtension(file: File, result: FileValidationResult): void {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (!extension) {
      result.errors.push('File has no extension');
      return;
    }

    if (this.DANGEROUS_EXTENSIONS.includes(extension)) {
      result.errors.push(`Dangerous file extension: .${extension}`);
      result.securityRisk = 'CRITICAL';
    }
  }

  /**
   * Validate MIME type
   */
  private validateMimeType(file: File, result: FileValidationResult): void {
    const allowedMimeTypes = Object.keys(this.MAGIC_NUMBERS);
    
    if (!allowedMimeTypes.includes(file.type)) {
      result.errors.push(`Unsupported MIME type: ${file.type}`);
    }
  }

  /**
   * Validate magic numbers (file signature)
   */
  private async validateMagicNumbers(file: File, result: FileValidationResult): Promise<void> {
    try {
      const buffer = await this.readFileHeader(file, 32); // Read first 32 bytes
      const bytes = new Uint8Array(buffer);

      let magicNumberMatch = false;
      let detectedType: string | null = null;

      for (const [mimeType, signatures] of Object.entries(this.MAGIC_NUMBERS)) {
        for (const signature of signatures) {
          if (this.matchesSignature(bytes, signature)) {
            magicNumberMatch = true;
            detectedType = mimeType;
            break;
          }
        }
        if (magicNumberMatch) break;
      }

      if (magicNumberMatch) {
        result.detectedType = detectedType;
        
        // Check if detected type matches declared MIME type
        if (detectedType !== file.type) {
          result.warnings.push(`MIME type mismatch: declared ${file.type}, detected ${detectedType}`);
          result.securityRisk = 'MEDIUM';
        }
      } else {
        result.errors.push('File signature does not match declared type (possible spoofing)');
        result.securityRisk = 'HIGH';
      }
    } catch (error) {
      console.error('File signature validation error:', error);
      result.errors.push('Failed to read file signature');
      result.securityRisk = 'HIGH';
    }
  }

  /**
   * Scan file content for malicious patterns
   */
  private async scanFileContent(file: File, result: FileValidationResult): Promise<void> {
    try {
      // Only scan text-based files and SVGs for malicious content
      if (file.type.includes('svg') || file.type.includes('text')) {
        const text = await this.readFileAsText(file);
        
        this.checkDangerousPatterns(text, result);
        this.checkSuspiciousStrings(text, result);
      }
    } catch (error) {
      // If we can't read the file as text, that's actually expected for binary files
      console.debug('Could not scan file content (likely binary file):', error);
    }
  }

  /**
   * Check for dangerous patterns in file content
   */
  private checkDangerousPatterns(text: string, result: FileValidationResult): void {
    for (const pattern of this.DANGEROUS_PATTERNS) {
      if (pattern.test(text)) {
        result.errors.push('Malicious content detected in file');
        result.securityRisk = 'CRITICAL';
        break;
      }
    }
  }

  /**
   * Check for suspicious strings in file content
   */
  private checkSuspiciousStrings(text: string, result: FileValidationResult): void {
    const suspiciousStrings = ['eval(', 'document.cookie', 'location.href', 'window.open'];
    for (const suspicious of suspiciousStrings) {
      if (text.toLowerCase().includes(suspicious)) {
        result.warnings.push(`Suspicious content detected: ${suspicious}`);
        if (result.securityRisk === 'LOW') {
          result.securityRisk = 'MEDIUM';
        }
      }
    }
  }

  /**
   * Validate file size based on type
   */
  private validateFileSize(file: File, result: FileValidationResult): void {
    const fileCategory = this.getFileCategory(file.type);
    const maxSize = this.MAX_FILE_SIZES[fileCategory];

    if (maxSize && file.size > maxSize) {
      result.errors.push(`File too large for ${fileCategory} (max ${this.formatFileSize(maxSize)})`);
    }
  }

  /**
   * Assess overall security risk
   */
  private assessSecurityRisk(result: FileValidationResult): void {
    if (result.errors.some(e => e.includes('Malicious') || e.includes('Dangerous'))) {
      result.securityRisk = 'CRITICAL';
    } else if (result.errors.some(e => e.includes('spoofing') || e.includes('signature'))) {
      result.securityRisk = 'HIGH';
    } else if (result.warnings.length > 0) {
      result.securityRisk = 'MEDIUM';
    } else {
      result.securityRisk = 'LOW';
    }
  }

  /**
   * Helper methods
   */
  private async readFileHeader(file: File, bytes: number): Promise<ArrayBuffer> {
    return file.slice(0, bytes).arrayBuffer();
  }

  private async readFileAsText(file: File): Promise<string> {
    return file.text();
  }

  private matchesSignature(bytes: Uint8Array, signature: (number | null)[]): boolean {
    if (signature.length > bytes.length) return false;
    
    for (let i = 0; i < signature.length; i++) {
      if (signature[i] !== null && bytes[i] !== signature[i]) {
        return false;
      }
    }
    return true;
  }

  private getFileCategory(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'unknown';
  }

  private formatFileSize(bytes: number): string {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  }
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  detectedType: string | null;
  securityRisk: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}